import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Box, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Collapse,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Link,
  LinearProgress,
  CardHeader,
  ListItemIcon,
  Divider,
  Pagination,
  Stack,
  Button,
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  Notifications as NotificationsIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  Chat as ChatIcon,
  Campaign as CampaignIcon,
  Assessment as AssessmentIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { getProductStats, getLeadStats } from '../services/mockData';
import { setProducts } from '../redux/slices/productSlice';
import { setLeads } from '../redux/slices/leadSlice';
import * as mockDataService from '../services/mockData';
import { Link as RouterLink } from 'react-router-dom';
import LeadStatusIndicator from '../components/LeadStatusIndicator';

const HomePage: React.FC = () => {
  const dispatch = useDispatch();
  const products = useSelector((state: RootState) => state.products.items);
  const leads = useSelector((state: RootState) => state.leads.items);
  const [stats, setStats] = useState({
    productCount: 0,
    leadCount: 0,
    conversionRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [showProducts, setShowProducts] = useState(false);
  const [showLeads, setShowLeads] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [leadPage, setLeadPage] = useState(1);
  const itemsPerPage = 20;
  const navigate = useNavigate();

  const statsData = [
    { title: 'Vendas Hoje', value: 'R$ 1.234,56', icon: <TrendingUpIcon />, color: '#4caf50' },
    { title: 'Novos Leads', value: '12', icon: <PeopleIcon />, color: '#2196f3' },
    { title: 'Produtos Vendidos', value: '8', icon: <ShoppingCartIcon />, color: '#ff9800' },
    { title: 'Receita Mensal', value: 'R$ 45.678,90', icon: <AttachMoneyIcon />, color: '#9c27b0' },
  ];

  const recentActivities = [
    { text: 'Novo lead registrado', time: '5 minutos atrás', icon: <PeopleIcon />, type: 'lead' },
    { text: 'Venda realizada', time: '15 minutos atrás', icon: <ShoppingCartIcon />, type: 'sale' },
    { text: 'Atualização de perfil', time: '1 hora atrás', icon: <StarIcon />, type: 'profile' },
    { text: 'Alerta de estoque baixo', time: '2 horas atrás', icon: <WarningIcon />, type: 'alert' },
    { text: 'Nova mensagem recebida', time: '3 horas atrás', icon: <ChatIcon />, type: 'message' },
    { text: 'Campanha iniciada', time: '4 horas atrás', icon: <CampaignIcon />, type: 'campaign' },
    { text: 'Relatório gerado', time: '5 horas atrás', icon: <AssessmentIcon />, type: 'report' },
    { text: 'Agendamento confirmado', time: '6 horas atrás', icon: <EventIcon />, type: 'schedule' },
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Carregar produtos
        const productsData = await mockDataService.getProducts();
        dispatch(setProducts(productsData));
        
        // Carregar leads
        const leadsData = await mockDataService.getLeads();
        dispatch(setLeads(leadsData));
        
        // Carregar estatísticas
        const productStats = await getProductStats();
        const leadStats = await getLeadStats();
        
        setStats({
          productCount: productStats.total,
          leadCount: leadStats.total,
          conversionRate: leadStats.conversionRate
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [dispatch]);

  const handleToggleProducts = () => {
    setShowProducts(!showProducts);
    if (showLeads) setShowLeads(false);
  };

  const handleToggleLeads = () => {
    setShowLeads(!showLeads);
    if (showProducts) setShowProducts(false);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'novo':
        return 'info';
      case 'em andamento':
        return 'warning';
      case 'convertido':
        return 'success';
      case 'perdido':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (product.description && product.description.toLowerCase().includes(productSearch.toLowerCase()))
  );

  const filteredLeads = leads.filter(lead =>
    lead.name.toLowerCase().includes(leadSearch.toLowerCase()) ||
    lead.email.toLowerCase().includes(leadSearch.toLowerCase()) ||
    lead.source.toLowerCase().includes(leadSearch.toLowerCase())
  );

  // Calcular produtos paginados
  const paginatedProducts = filteredProducts.slice(
    (productPage - 1) * itemsPerPage,
    productPage * itemsPerPage
  );
  
  // Calcular leads paginados
  const paginatedLeads = filteredLeads.slice(
    (leadPage - 1) * itemsPerPage,
    leadPage * itemsPerPage
  );

  // Calcular número total de páginas
  const totalProductPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const totalLeadPages = Math.ceil(filteredLeads.length / itemsPerPage);

  // Resetar páginas quando a busca mudar
  useEffect(() => {
    setProductPage(1);
  }, [productSearch]);

  useEffect(() => {
    setLeadPage(1);
  }, [leadSearch]);

  const convertLeadStatus = (status: string): 'active' | 'warning' | 'critical' => {
    switch (status) {
      case 'Novo':
      case 'Qualificado':
        return 'active';
      case 'Contatado':
        return 'warning';
      case 'Perdido':
        return 'critical';
      default:
        return 'active';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box py={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {/* Estatísticas */}
              {statsData.map((stat) => (
                <Grid item xs={12} sm={6} md={3} key={stat.title}>
                  <Paper
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      height: 140,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        p: 1,
                        color: stat.color,
                        opacity: 0.2,
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Typography color="textSecondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {stat.value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}

              {/* Produtos Cadastrados */}
              <Grid item xs={12} md={4}>
                <Paper 
                  sx={{ p: 2, cursor: 'pointer' }} 
                  onClick={handleToggleProducts}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">Produtos</Typography>
                      <Typography variant="h4">{products.length}</Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={70} 
                        sx={{ 
                          mt: 1, 
                          height: 4, 
                          borderRadius: 2,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#ff9800',
                          }
                        }} 
                      />
                    </Box>
                    <IconButton size="small">
                      {showProducts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>

              {/* Leads Cadastrados */}
              <Grid item xs={12} md={4}>
                <Paper 
                  sx={{ p: 2, cursor: 'pointer' }} 
                  onClick={handleToggleLeads}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">Leads</Typography>
                      <Typography variant="h4">{leads.length}</Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={85} 
                        sx={{ 
                          mt: 1, 
                          height: 4, 
                          borderRadius: 2,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#2196f3',
                          }
                        }} 
                      />
                    </Box>
                    <IconButton size="small">
                      {showLeads ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>

              {/* Atividades Recentes */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Atividades Recentes"
                    avatar={<NotificationsIcon />}
                  />
                  <CardContent>
                    <Grid container spacing={1}>
                      {recentActivities.map((activity, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 1.5, 
                              height: '100%',
                              backgroundColor: 'background.default',
                              borderRadius: 2,
                              transition: 'all 0.2s',
                              border: '1px solid',
                              borderColor: (theme) => {
                                switch(activity.type) {
                                  case 'lead': return theme.palette.info.light;
                                  case 'sale': return theme.palette.success.light;
                                  case 'profile': return theme.palette.warning.light;
                                  case 'alert': return theme.palette.error.light;
                                  case 'message': return theme.palette.primary.light;
                                  case 'campaign': return theme.palette.secondary.light;
                                  case 'report': return theme.palette.info.light;
                                  case 'schedule': return theme.palette.success.light;
                                  default: return theme.palette.divider;
                                }
                              },
                              '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: 2,
                                backgroundColor: (theme) => {
                                  switch(activity.type) {
                                    case 'lead': return theme.palette.info.light;
                                    case 'sale': return theme.palette.success.light;
                                    case 'profile': return theme.palette.warning.light;
                                    case 'alert': return theme.palette.error.light;
                                    case 'message': return theme.palette.primary.light;
                                    case 'campaign': return theme.palette.secondary.light;
                                    case 'report': return theme.palette.info.light;
                                    case 'schedule': return theme.palette.success.light;
                                    default: return theme.palette.background.default;
                                  }
                                }
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  backgroundColor: (theme) => {
                                    switch(activity.type) {
                                      case 'lead': return theme.palette.info.main;
                                      case 'sale': return theme.palette.success.main;
                                      case 'profile': return theme.palette.warning.main;
                                      case 'alert': return theme.palette.error.main;
                                      case 'message': return theme.palette.primary.main;
                                      case 'campaign': return theme.palette.secondary.main;
                                      case 'report': return theme.palette.info.main;
                                      case 'schedule': return theme.palette.success.main;
                                      default: return theme.palette.primary.main;
                                    }
                                  },
                                  color: 'white',
                                  mr: 1.5
                                }}
                              >
                                {activity.icon}
                              </Box>
                              <Box sx={{ overflow: 'hidden' }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight="medium"
                                  noWrap
                                  sx={{ 
                                    color: (theme) => {
                                      switch(activity.type) {
                                        case 'lead': return theme.palette.info.dark;
                                        case 'sale': return theme.palette.success.dark;
                                        case 'profile': return theme.palette.warning.dark;
                                        case 'alert': return theme.palette.error.dark;
                                        case 'message': return theme.palette.primary.dark;
                                        case 'campaign': return theme.palette.secondary.dark;
                                        case 'report': return theme.palette.info.dark;
                                        case 'schedule': return theme.palette.success.dark;
                                        default: return theme.palette.text.primary;
                                      }
                                    }
                                  }}
                                >
                                  {activity.text}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {activity.time}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Cards de Produtos */}
            <Collapse in={showProducts} timeout="auto" unmountOnExit>
              <Paper sx={{ mt: 3, p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6">
                    Produtos Cadastrados
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Buscar produtos..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    sx={{ width: 250 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 2,
                  mt: 2 
                }}>
                  {paginatedProducts.map((product) => (
                    <Card 
                      key={product.id}
                      sx={{ 
                        width: 200,
                        minHeight: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                          transition: 'all 0.2s'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Avatar
                            variant="rounded"
                            src={product.imageUrl}
                            alt={product.name}
                            sx={{ 
                              width: 32, 
                              height: 32,
                              bgcolor: 'primary.main'
                            }}
                          />
                          <Typography 
                            variant="body2" 
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                          >
                            R$ {product.price.toFixed(2)}
                          </Typography>
                        </Box>
                        <Link
                          component={RouterLink}
                          to={`/produtos/${product.id}`}
                          sx={{
                            textDecoration: 'none',
                            color: 'text.primary',
                            fontWeight: 'medium',
                            '&:hover': {
                              color: 'primary.main'
                            }
                          }}
                        >
                          <Typography noWrap title={product.name}>
                            {product.name}
                          </Typography>
                        </Link>
                        {product.description && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            noWrap
                            title={product.description}
                          >
                            {product.description}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {filteredProducts.length === 0 && (
                    <Typography color="text.secondary" textAlign="center" py={2}>
                      Nenhum produto encontrado
                    </Typography>
                  )}
                </Box>
                
                {/* Paginação de Produtos */}
                {filteredProducts.length > itemsPerPage && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Stack spacing={2}>
                      <Pagination 
                        count={totalProductPages} 
                        page={productPage} 
                        onChange={(e, page) => setProductPage(page)} 
                        color="primary" 
                        showFirstButton 
                        showLastButton
                      />
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Collapse>

            {/* Cards de Leads */}
            <Collapse in={showLeads} timeout="auto" unmountOnExit>
              <Paper sx={{ mt: 3, p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6">
                    Leads Cadastrados
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Buscar leads..."
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                    sx={{ width: 250 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 2,
                  mt: 2 
                }}>
                  {paginatedLeads.map((lead) => (
                    <Card 
                      key={lead.id}
                      sx={{ 
                        width: 200,
                        minHeight: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                          transition: 'all 0.2s'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32,
                              bgcolor: 'primary.main'
                            }}
                          >
                            {lead.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Chip 
                              label={lead.status} 
                              size="small"
                              color={getStatusColor(lead.status) as any}
                            />
                            <Box 
                              sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                ml: 1,
                                backgroundColor: (theme) => {
                                  switch(lead.status.toLowerCase()) {
                                    case 'novo': return theme.palette.info.main;
                                    case 'em andamento': return theme.palette.warning.main;
                                    case 'convertido': return theme.palette.success.main;
                                    case 'perdido': return theme.palette.error.main;
                                    default: return theme.palette.grey[500];
                                  }
                                }
                              }} 
                            />
                          </Box>
                        </Box>
                        <Link
                          component={RouterLink}
                          to={`/leads/${lead.id}`}
                          sx={{
                            textDecoration: 'none',
                            color: 'text.primary',
                            fontWeight: 'medium',
                            '&:hover': {
                              color: 'primary.main'
                            }
                          }}
                        >
                          <Typography noWrap title={lead.name}>
                            {lead.name}
                          </Typography>
                        </Link>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          noWrap
                          title={lead.email}
                        >
                          {lead.email}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                        >
                          Origem: {lead.source}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                  {filteredLeads.length === 0 && (
                    <Typography color="text.secondary" textAlign="center" py={2}>
                      Nenhum lead encontrado
                    </Typography>
                  )}
                </Box>
                
                {/* Paginação de Leads */}
                {filteredLeads.length > itemsPerPage && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Stack spacing={2}>
                      <Pagination 
                        count={totalLeadPages} 
                        page={leadPage} 
                        onChange={(e, page) => setLeadPage(page)} 
                        color="primary" 
                        showFirstButton 
                        showLastButton
                      />
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Collapse>

            {/* Barras de Temperatura e Produtos */}
            <Paper sx={{ p: 3, mt: 3 }}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h6" sx={{ minWidth: 200 }}>
                    Temperatura dos Leads
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={stats.conversionRate}
                      sx={{
                        height: 15,
                        borderRadius: 5,
                        backgroundColor: '#e0e0e0',
                        flexGrow: 1,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: (theme) => 
                            stats.conversionRate < 30 ? theme.palette.error.main :
                            stats.conversionRate < 70 ? theme.palette.warning.main :
                            theme.palette.success.main,
                          borderRadius: 5,
                        },
                      }}
                    />
                    <Typography variant="h6" sx={{ 
                      color: (theme) =>
                        stats.conversionRate < 30 ? theme.palette.error.main :
                        stats.conversionRate < 70 ? theme.palette.warning.main :
                        theme.palette.success.main,
                      minWidth: 70,
                      fontWeight: 'bold'
                    }}>
                      {stats.conversionRate.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h6" sx={{ minWidth: 200 }}>
                    Produtos Solicitados
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={85}
                      sx={{
                        height: 15,
                        borderRadius: 5,
                        backgroundColor: '#e0e0e0',
                        flexGrow: 1,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: (theme) => theme.palette.info.main,
                          borderRadius: 5,
                        },
                      }}
                    />
                    <Typography variant="h6" sx={{ 
                      color: (theme) => theme.palette.info.main,
                      minWidth: 70,
                      fontWeight: 'bold'
                    }}>
                      85%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate('/dashboard')}
              >
                Acessar Dashboard Atualizado
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default HomePage; 